import React, { useState, useEffect } from 'react';
import { apiUrl, type UnitScale, unitLabel } from '../App';
import { authedFetch } from '../supabaseClient';
import KpiTooltip from '../KpiTooltip';

// ── Types ─────────────────────────────────────────────────────────────────────

interface CategoryRow {
  category: string;
  values: Record<string, number>;
}

interface SectionRow {
  section: string;
  total: Record<string, number>;
  categories: CategoryRow[];
  is_subtotal: boolean;
}

interface MetricCategoryRow {
  category: string;
  values: Record<string, number | null>;
}

interface MetricSectionRow {
  section: string;
  values: Record<string, number | null>;
  categories: MetricCategoryRow[];
}

interface Margins {
  has_net_revenue_margin: boolean;
  has_net_profit_margin:  boolean;
  net_revenue_margin:     Record<string, number | null>;
  gross_margin:           Record<string, number | null>;
  ebitda_margin:          Record<string, number | null>;
  net_profit_margin:      Record<string, number | null>;
}

interface CapEffPeriod {
  rrr_added_pnl:     number | null;
  quarterly_ebitda:  number | null;
  burn_multiple_pnl: number | null;
  rrr_burn_pnl:      number | null;
}

interface SalesEffPeriod {
  quarterly_sm: number | null;
  magic_pnl:    number | null;
}

interface OperatingMetrics {
  yoy_growth:          { revenue_sections: MetricSectionRow[]; has_raw_data_placeholder: boolean };
  ttm_yoy_growth:      { revenue_sections: MetricSectionRow[]; has_raw_data_placeholder: boolean };
  run_rate_revenue:    { revenue_sections: MetricSectionRow[]; has_raw_data_placeholder: boolean };
  ttm_revenue:         { revenue_sections: MetricSectionRow[]; has_raw_data_placeholder: boolean };
  revenue_mix:         { revenue_sections: MetricSectionRow[]; has_revenue_mix: boolean };
  margins:             Margins;
  capital_efficiency:  Record<string, CapEffPeriod>;
  sales_efficiency:    Record<string, SalesEffPeriod>;
  pct_of_revenue: {
    section_label:  string;
    total:          Record<string, number | null>;
    categories:     MetricCategoryRow[];
    has_categories: boolean;
  };
  rule_of_40: {
    yoy: Record<string, number | null>;
    ttm: Record<string, number | null>;
  };
  cash_runway: {
    has_cash:      boolean;
    has_cfo:       boolean;
    cash_balance:  Record<string, number>;
    ebitda_values: Record<string, number>;
    cfo_values:    Record<string, number>;
    ebitda:        Record<string, number | null>;
    cfo:           Record<string, number | null>;
  };
  volume_metrics: {
    volume_label:   string;
    take_rate:      Record<string, number | null>;
    gp_take_rate:   Record<string, number | null>;
    yoy_growth:     Record<string, number | null>;
    ttm_yoy_growth: Record<string, number | null>;
    run_rate:       Record<string, number | null>;
  } | null;
}

interface PnlData {
  periods: string[];
  period_type: string;
  has_contra: boolean;
  has_below_ebitda: boolean;
  has_volume_metrics: boolean;
  volume_label: string | null;
  gross_revenue: Record<string, number>;
  contra_revenue: Record<string, number>;
  net_revenue: Record<string, number>;
  cogs: Record<string, number>;
  gross_profit: Record<string, number>;
  total_opex: Record<string, number>;
  ebitda: Record<string, number>;
  other_income_expense: Record<string, number>;
  net_income: Record<string, number>;
  gross_margin_pct: Record<string, number>;
  ebitda_margin_pct: Record<string, number>;
  sections: SectionRow[];
  operating_metrics: OperatingMetrics;
  balance_sheet_sections: SectionRow[];
  cash_flow_sections:     SectionRow[];
}

// ── Customer Analytics Types ─────────────────────────────────────────────────

interface MrrBuildPeriod {
  beginning: number;
  new: number;
  resurrection: number;
  upsell: number;
  contraction: number;
  churned: number;
  net_change: number;
  ending: number;
  net_churn_pct: number | null;
  gross_churn_pct: number | null;
}

interface CustomerBuildPeriod {
  beginning: number;
  additions: number;
  subtractions: number;
  ending: number;
  churn_pct: number | null;
}

interface CustomerAnalytics {
  period_cols: string[];
  period_type: string;
  mrr_build: Record<string, MrrBuildPeriod>;
  customer_build: Record<string, CustomerBuildPeriod>;
  nrr: Record<string, number | null>;
  grr: Record<string, number | null>;
  summary: {
    total_revenue: Record<string, number>;
    run_rate_revenue: Record<string, number>;
    ttm_revenue: Record<string, number | null>;
    yoy_growth: Record<string, number | null>;
    ttm_yoy_growth: Record<string, number | null>;
    customer_count: Record<string, number>;
    total_arr: number;
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatPeriod(period: string): string {
  const [year, month] = period.split('-');
  return `${parseInt(month)}/${year}`;
}

let _scale = 1000;

function formatVal(val: number): string {
  if (val === 0) return '';
  const abs = Math.abs(val) / _scale;
  const str = abs < 1 ? abs.toFixed(1) : Math.round(abs).toLocaleString();
  return val < 0 ? `(${str})` : str;
}

function formatPct(val: number | null | undefined): string {
  if (val === null || val === undefined) return '';
  const rounded = Math.round(Math.abs(val));
  const str = rounded === 0 ? '0%' : rounded + '%';
  return val < 0 ? `(${str})` : str;
}

function formatMultiple(val: number | null | undefined): string {
  if (val === null || val === undefined) return '';
  return val.toFixed(1) + 'x';
}

function formatMonths(val: number | null | undefined): string {
  if (val === null || val === undefined) return '';
  return val.toFixed(1) + 'mo';
}

function formatCount(val: number | null | undefined): string {
  if (val === null || val === undefined || val === 0) return '';
  return val.toLocaleString();
}

// ── Row components ────────────────────────────────────────────────────────────

// Dark navy section label — visually separates major dashboard sections
function SectionLabelRow({ label, periods }: {
  label: string;
  periods: string[];
}) {
  return (
    <tr className="border-b border-gray-700">
      <td className="sticky left-0 z-10 bg-gray-950 px-3 md:px-5 py-3 whitespace-nowrap">
        <span className="font-bold text-white text-[11px] md:text-sm tracking-wide uppercase">{label}</span>
      </td>
      {periods.map(p => (
        <td key={p} className="bg-gray-950" />
      ))}
    </tr>
  );
}

// Collapsible P&L section (Revenue, COGS, etc.) + indented category rows
function PnlSectionBlock({ sec, periods, collapsed, onToggle }: {
  sec: SectionRow;
  periods: string[];
  collapsed: boolean;
  onToggle: () => void;
}) {
  const hasCategories = sec.categories.length > 0;
  return (
    <>
      <tr
        className={`border-b border-gray-700 bg-[#161e2d] ${hasCategories ? 'cursor-pointer hover:bg-[#192230]' : ''}`}
        onClick={hasCategories ? onToggle : undefined}
      >
        <td className="sticky left-0 z-10 bg-[#161e2d] px-3 md:px-5 py-2.5 whitespace-nowrap">
          <div className="flex items-center gap-2">
            <span className="text-gray-500 text-[11px] md:text-xs select-none w-3 inline-block">
              {hasCategories ? (collapsed ? '▶' : '▼') : ''}
            </span>
            <span className="font-bold text-white text-[11px] md:text-xs">{sec.section}</span>
          </div>
        </td>
        {periods.map(p => (
          <td key={p} className="text-right px-3 md:px-6 py-2.5 font-bold text-white whitespace-nowrap">
            {formatVal(sec.total[p] ?? 0)}
          </td>
        ))}
      </tr>
      {hasCategories && !collapsed && sec.categories.map(cat => (
        <tr key={`cat-${sec.section}-${cat.category}`} className="border-b border-gray-800/60 hover:bg-gray-800/30">
          <td className="sticky left-0 z-10 bg-gray-900 pl-5 md:pl-10 pr-3 md:pr-5 py-2 text-gray-400 md:whitespace-nowrap text-[11px] md:text-xs">{cat.category}</td>
          {periods.map(p => (
            <td key={p} className="text-right px-3 md:px-6 py-2 text-gray-300 whitespace-nowrap">
              {formatVal(cat.values[p] ?? 0)}
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

// Bold calculated subtotal (Gross Profit, EBITDA, Net Income)
function PnlSubtotalRow({ label, values, periods, kpiKey, projectId }: {
  label: string;
  values: Record<string, number>;
  periods: string[];
  kpiKey?: string;
  projectId?: string;
}) {
  return (
    <tr className="border-b border-gray-700 bg-[#131b2a]">
      <td className="sticky left-0 z-10 bg-[#131b2a] px-3 md:px-5 py-2.5 font-bold text-white whitespace-nowrap text-[11px] md:text-xs">{label}</td>
      {periods.map(p => (
        <td key={p} className="text-right px-3 md:px-6 py-2.5 font-bold text-white whitespace-nowrap">
          {kpiKey && projectId
            ? <KpiTooltip kpiKey={kpiKey} projectId={projectId}>{formatVal(values[p] ?? 0)}</KpiTooltip>
            : formatVal(values[p] ?? 0)}
        </td>
      ))}
    </tr>
  );
}

// Collapsible metric group header (YoY Growth, TTM YoY Growth)
function MetricGroupRow({ label, collapsed, onToggle, periods }: {
  label: string;
  collapsed: boolean;
  onToggle: () => void;
  periods: string[];
}) {
  return (
    <tr className="border-b border-gray-700 bg-[#161e2d] cursor-pointer hover:bg-[#192230]" onClick={onToggle}>
      <td className="sticky left-0 z-10 bg-[#161e2d] px-3 md:px-5 py-2.5 whitespace-nowrap">
        <div className="flex items-center gap-2">
          <span className="text-gray-500 text-[11px] md:text-xs select-none w-3 inline-block">{collapsed ? '▶' : '▼'}</span>
          <span className="font-bold text-white text-[11px] md:text-xs">{label}</span>
        </div>
      </td>
      {periods.map(p => <td key={p} className="py-2.5" />)}
    </tr>
  );
}

// Blank placeholder row — shows label, all values empty (future data)
function PlaceholderRow({ label, periods, indent }: { label: string; periods: string[]; indent?: boolean }) {
  return (
    <tr className="border-b border-gray-800/40">
      <td className={`sticky left-0 z-10 bg-gray-900 ${indent ? 'pl-6 md:pl-[68px]' : 'pl-5 md:pl-[52px]'} pr-3 md:pr-5 py-2 text-gray-500 text-[11px] md:text-xs md:whitespace-nowrap`}>{label}</td>
      {periods.map(p => <td key={p} className="text-right px-3 md:px-6 py-2 text-gray-600 text-[11px] md:text-xs whitespace-nowrap"></td>)}
    </tr>
  );
}

// ── Dashboard Table ───────────────────────────────────────────────────────────
// Single unified table — sticky date header + sticky left column.
// Add new major sections here as they are built (Customer Build, Balance Sheet, CFS).

function DashboardTable({ pnl, customerData, projectId }: { pnl: PnlData; customerData: CustomerAnalytics | null; projectId: string }) {
  const { periods, sections, has_below_ebitda, operating_metrics, balance_sheet_sections, cash_flow_sections } = pnl;
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const toggle = (key: string) =>
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  // KPI tooltip wrapper — renders tooltip when kpiKey provided, plain content otherwise
  const Tip = ({ k, children }: { k?: string; children: React.ReactNode }) =>
    k ? <KpiTooltip kpiKey={k} projectId={projectId}>{children}</KpiTooltip> : <>{children}</>;

  const revSections = sections.filter(s =>
    ['Revenue', 'Gross Revenue', 'Contra Revenue', 'Net Revenue'].includes(s.section)
  );
  const cogsSec  = sections.find(s => s.section === 'COGS');
  const opexSec  = sections.find(s => s.section === 'Operating Expenses');
  const otherSec = sections.find(s => s.section === 'Other Income & Expenses');

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg">
      <div className="overflow-auto max-h-[calc(100vh-7rem)]">
        <table className="w-full text-[11px] md:text-xs">

          {/* ── Sticky date header ── */}
          <thead className="sticky top-0 z-20">
            <tr className="border-b border-gray-700 bg-gray-900">
              <th className="sticky left-0 z-30 bg-gray-900 text-left px-3 md:px-5 py-2.5 w-28 md:w-52 text-gray-500 text-[11px] md:text-xs font-medium">{unitLabel(_scale as UnitScale)}</th>
              {periods.map(p => (
                <th key={p} className="bg-gray-900 text-right px-3 md:px-6 py-2.5 font-bold text-gray-200 text-[11px] md:text-sm whitespace-nowrap min-w-[72px]">
                  {formatPeriod(p)}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>

            {/* ══ Financial Performance ══ */}
            <SectionLabelRow
              label="Financial Performance"
              periods={periods}
            />

            {/* Volume Metrics (conditional — GMV/TPV/AUM above Revenue) */}
            {pnl.has_volume_metrics && pnl.volume_label && (() => {
              const volSections = sections.filter(s => s.section === pnl.volume_label);
              return volSections.map(sec => (
                <PnlSectionBlock
                  key={sec.section}
                  sec={sec}
                  periods={periods}
                  collapsed={!!collapsed[sec.section]}
                  onToggle={() => toggle(sec.section)}
                />
              ));
            })()}

            {revSections.map(sec => (
              <PnlSectionBlock
                key={sec.section}
                sec={sec}
                periods={periods}
                collapsed={!!collapsed[sec.section]}
                onToggle={() => toggle(sec.section)}
              />
            ))}

            {cogsSec && (
              <PnlSectionBlock
                sec={cogsSec}
                periods={periods}
                collapsed={!!collapsed['COGS']}
                onToggle={() => toggle('COGS')}
              />
            )}

            <PnlSubtotalRow label="Gross Profit" values={pnl.gross_profit} periods={periods} kpiKey="gross_profit" projectId={projectId} />

            {opexSec && (
              <PnlSectionBlock
                sec={opexSec}
                periods={periods}
                collapsed={!!collapsed['Operating Expenses']}
                onToggle={() => toggle('Operating Expenses')}
              />
            )}

            <PnlSubtotalRow label="EBITDA" values={pnl.ebitda} periods={periods} kpiKey="ebitda" projectId={projectId} />

            {has_below_ebitda && otherSec && (
              <>
                <PnlSectionBlock
                  sec={otherSec}
                  periods={periods}
                  collapsed={!!collapsed['Other Income & Expenses']}
                  onToggle={() => toggle('Other Income & Expenses')}
                />
                <PnlSubtotalRow label="Net Income" values={pnl.net_income} periods={periods} kpiKey="net_income" projectId={projectId} />
              </>
            )}

            {/* ══ Operating Metrics ══ */}
            <SectionLabelRow label="Operating Metrics" periods={periods} />

            {/* Volume Metrics (conditional — only when GMV/TPV/AUM data exists) */}
            {operating_metrics.volume_metrics && (() => {
              const vm = operating_metrics.volume_metrics;
              const volLabel = vm.volume_label;
              const n = pnl.period_type === 'quarterly' ? 4 : 12;
              type VolRow =
                | { label: string; values: Record<string, number | null>; fmt: 'pct' | 'val'; kpiKey?: string }
                | { label: string; fmt: 'val'; source: 'customer'; kpiKey?: string };
              const rows: VolRow[] = [
                { label: `Take Rate (Revenue / ${volLabel})`,         values: vm.take_rate,      fmt: 'pct', kpiKey: 'take_rate' },
                { label: `GP Take Rate (Gross Profit / ${volLabel})`, values: vm.gp_take_rate,   fmt: 'pct', kpiKey: 'gp_take_rate' },
                { label: `${volLabel} YoY Growth`,                    values: vm.yoy_growth,     fmt: 'pct', kpiKey: 'volume_yoy_growth' },
                { label: `${volLabel} TTM YoY Growth`,                values: vm.ttm_yoy_growth, fmt: 'pct', kpiKey: 'volume_ttm_yoy_growth' },
                { label: `Run-Rate ${volLabel}`,                      values: vm.run_rate,       fmt: 'val', kpiKey: 'volume_run_rate' },
                { label: `${volLabel} per Customer`,                  fmt: 'val', source: 'customer' as const, kpiKey: 'volume_per_customer' },
              ];
              return (
                <>
                  <MetricGroupRow
                    label="Volume Metrics"
                    collapsed={!!collapsed['volmetrics']}
                    onToggle={() => toggle('volmetrics')}
                    periods={periods}
                  />
                  {!collapsed['volmetrics'] && rows.map((row, idx) => {
                    if ('source' in row && row.source === 'customer') {
                      // Volume per Customer — computed on frontend
                      return (
                        <tr key={idx} className="border-b border-gray-800/60 hover:bg-gray-800/30">
                          <td className="sticky left-0 z-10 bg-gray-900 pl-5 md:pl-[52px] pr-3 md:pr-5 py-2 text-gray-400 text-[11px] md:text-xs md:whitespace-nowrap">{row.label}</td>
                          {periods.map(p => {
                            if (!customerData) return <td key={p} className="text-right px-3 md:px-6 py-2 text-gray-300 text-[11px] md:text-xs whitespace-nowrap" />;
                            const volPeriod = (vm.run_rate[p] ?? 0) / n;
                            const custCount = customerData.customer_build?.[p]?.ending ?? 0;
                            const v = custCount > 0 ? volPeriod / custCount : null;
                            return (
                              <td key={p} className="text-right px-3 md:px-6 py-2 text-gray-300 text-[11px] md:text-xs whitespace-nowrap">
                                {v != null ? <Tip k={row.kpiKey}>{formatVal(v)}</Tip> : ''}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    }
                    if (!('values' in row)) return null;
                    return (
                      <tr key={idx} className="border-b border-gray-800/60 hover:bg-gray-800/30">
                        <td className="sticky left-0 z-10 bg-gray-900 pl-5 md:pl-[52px] pr-3 md:pr-5 py-2 text-gray-400 text-[11px] md:text-xs md:whitespace-nowrap">{row.label}</td>
                        {periods.map(p => {
                          const v = row.values[p];
                          const formatted = row.fmt === 'pct' ? formatPct(v) : (v != null ? formatVal(v) : '');
                          return (
                            <td key={p} className="text-right px-3 md:px-6 py-2 text-gray-300 text-[11px] md:text-xs whitespace-nowrap">
                              <Tip k={row.kpiKey}>{formatted}</Tip>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </>
              );
            })()}

            {/* YoY Growth */}
            <MetricGroupRow
              label="YoY Growth"
              collapsed={!!collapsed['yoy']}
              onToggle={() => toggle('yoy')}
              periods={periods}
            />
            {!collapsed['yoy'] && (
              <>
                {operating_metrics.yoy_growth.revenue_sections.map(sec => {
                  const secKey = `yoy-${sec.section}`;
                  return (
                    <React.Fragment key={secKey}>
                      <tr
                        className={`border-b border-gray-800/60 ${sec.categories.length > 0 ? 'cursor-pointer hover:bg-gray-800/30' : ''}`}
                        onClick={sec.categories.length > 0 ? () => toggle(secKey) : undefined}
                      >
                        <td className="sticky left-0 z-10 bg-gray-900 pl-4 md:pl-8 pr-3 md:pr-5 py-2 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 text-[11px] md:text-xs select-none w-3 inline-block">
                              {sec.categories.length > 0 ? (collapsed[secKey] ? '▶' : '▼') : ''}
                            </span>
                            <span className="font-bold text-white text-[11px] md:text-xs">{sec.section}</span>
                          </div>
                        </td>
                        {periods.map(p => (
                          <td key={p} className="text-right px-3 md:px-6 py-2 font-bold text-white text-[11px] md:text-xs whitespace-nowrap">
                            <Tip k="yoy_revenue_growth">{formatPct(sec.values[p])}</Tip>
                          </td>
                        ))}
                      </tr>
                      {!collapsed[secKey] && sec.categories.map(cat => (
                        <tr key={`${secKey}-${cat.category}`} className="border-b border-gray-800/40 hover:bg-[#131b2a]">
                          <td className="sticky left-0 z-10 bg-gray-900 pl-6 md:pl-16 pr-3 md:pr-5 py-1.5 text-gray-400 text-[11px] md:text-xs md:whitespace-nowrap">{cat.category}</td>
                          {periods.map(p => (
                            <td key={p} className="text-right px-3 md:px-6 py-1.5 text-gray-300 text-[11px] md:text-xs whitespace-nowrap">
                              {formatPct(cat.values[p])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
                <tr className="border-b border-gray-800/60 hover:bg-gray-800/30">
                  <td className="sticky left-0 z-10 bg-gray-900 pl-5 md:pl-[52px] pr-3 md:pr-5 py-2 text-gray-400 text-[11px] md:text-xs md:whitespace-nowrap">Revenue per Customer Data</td>
                  {periods.map(p => (
                    <td key={p} className="text-right px-3 md:px-6 py-2 text-gray-300 text-[11px] md:text-xs whitespace-nowrap">
                      {customerData ? <Tip k="yoy_revenue_growth">{formatPct(customerData.summary.yoy_growth[p])}</Tip> : ''}
                    </td>
                  ))}
                </tr>
              </>
            )}

            {/* TTM YoY Growth */}
            <MetricGroupRow
              label="TTM YoY Growth"
              collapsed={!!collapsed['ttm']}
              onToggle={() => toggle('ttm')}
              periods={periods}
            />
            {!collapsed['ttm'] && (
              <>
                {operating_metrics.ttm_yoy_growth.revenue_sections.map(sec => {
                  const secKey = `ttm-${sec.section}`;
                  return (
                    <React.Fragment key={secKey}>
                      <tr
                        className={`border-b border-gray-800/60 ${sec.categories.length > 0 ? 'cursor-pointer hover:bg-gray-800/30' : ''}`}
                        onClick={sec.categories.length > 0 ? () => toggle(secKey) : undefined}
                      >
                        <td className="sticky left-0 z-10 bg-gray-900 pl-4 md:pl-8 pr-3 md:pr-5 py-2 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 text-[11px] md:text-xs select-none w-3 inline-block">
                              {sec.categories.length > 0 ? (collapsed[secKey] ? '▶' : '▼') : ''}
                            </span>
                            <span className="font-bold text-white text-[11px] md:text-xs">{sec.section}</span>
                          </div>
                        </td>
                        {periods.map(p => (
                          <td key={p} className="text-right px-3 md:px-6 py-2 font-bold text-white text-[11px] md:text-xs whitespace-nowrap">
                            <Tip k="ttm_yoy_revenue_growth">{formatPct(sec.values[p])}</Tip>
                          </td>
                        ))}
                      </tr>
                      {!collapsed[secKey] && sec.categories.map(cat => (
                        <tr key={`${secKey}-${cat.category}`} className="border-b border-gray-800/40 hover:bg-[#131b2a]">
                          <td className="sticky left-0 z-10 bg-gray-900 pl-6 md:pl-16 pr-3 md:pr-5 py-1.5 text-gray-400 text-[11px] md:text-xs md:whitespace-nowrap">{cat.category}</td>
                          {periods.map(p => (
                            <td key={p} className="text-right px-3 md:px-6 py-1.5 text-gray-300 text-[11px] md:text-xs whitespace-nowrap">
                              {formatPct(cat.values[p])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
                <tr className="border-b border-gray-800/60 hover:bg-gray-800/30">
                  <td className="sticky left-0 z-10 bg-gray-900 pl-5 md:pl-[52px] pr-3 md:pr-5 py-2 text-gray-400 text-[11px] md:text-xs md:whitespace-nowrap">Revenue per Customer Data</td>
                  {periods.map(p => (
                    <td key={p} className="text-right px-3 md:px-6 py-2 text-gray-300 text-[11px] md:text-xs whitespace-nowrap">
                      {customerData ? <Tip k="ttm_yoy_revenue_growth">{formatPct(customerData.summary.ttm_yoy_growth[p])}</Tip> : ''}
                    </td>
                  ))}
                </tr>
              </>
            )}

            {/* Run-Rate Revenue */}
            <MetricGroupRow
              label="Run-Rate Revenue"
              collapsed={!!collapsed['rrr']}
              onToggle={() => toggle('rrr')}
              periods={periods}
            />
            {!collapsed['rrr'] && (
              <>
                {operating_metrics.run_rate_revenue.revenue_sections.map(sec => {
                  const secKey = `rrr-${sec.section}`;
                  return (
                    <React.Fragment key={secKey}>
                      <tr
                        className={`border-b border-gray-800/60 ${sec.categories.length > 0 ? 'cursor-pointer hover:bg-gray-800/30' : ''}`}
                        onClick={sec.categories.length > 0 ? () => toggle(secKey) : undefined}
                      >
                        <td className="sticky left-0 z-10 bg-gray-900 pl-4 md:pl-8 pr-3 md:pr-5 py-2 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 text-[11px] md:text-xs select-none w-3 inline-block">
                              {sec.categories.length > 0 ? (collapsed[secKey] ? '▶' : '▼') : ''}
                            </span>
                            <span className="font-bold text-white text-[11px] md:text-xs">{sec.section}</span>
                          </div>
                        </td>
                        {periods.map(p => (
                          <td key={p} className="text-right px-3 md:px-6 py-2 font-bold text-white text-[11px] md:text-xs whitespace-nowrap">
                            <Tip k="run_rate_revenue">{formatVal(sec.values[p] ?? 0)}</Tip>
                          </td>
                        ))}
                      </tr>
                      {!collapsed[secKey] && sec.categories.map(cat => (
                        <tr key={`${secKey}-${cat.category}`} className="border-b border-gray-800/40 hover:bg-[#131b2a]">
                          <td className="sticky left-0 z-10 bg-gray-900 pl-6 md:pl-16 pr-3 md:pr-5 py-1.5 text-gray-400 text-[11px] md:text-xs md:whitespace-nowrap">{cat.category}</td>
                          {periods.map(p => (
                            <td key={p} className="text-right px-3 md:px-6 py-1.5 text-gray-300 text-[11px] md:text-xs whitespace-nowrap">
                              {formatVal(cat.values[p] ?? 0)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
                <tr className="border-b border-gray-800/60 hover:bg-gray-800/30">
                  <td className="sticky left-0 z-10 bg-gray-900 pl-5 md:pl-[52px] pr-3 md:pr-5 py-2 text-gray-400 text-[11px] md:text-xs md:whitespace-nowrap">Revenue per Customer Data</td>
                  {periods.map(p => (
                    <td key={p} className="text-right px-3 md:px-6 py-2 text-gray-300 text-[11px] md:text-xs whitespace-nowrap">
                      {customerData ? <Tip k="run_rate_revenue">{formatVal(customerData.summary.run_rate_revenue[p] ?? 0)}</Tip> : ''}
                    </td>
                  ))}
                </tr>
              </>
            )}

            {/* TTM Revenue */}
            <MetricGroupRow
              label="TTM Revenue"
              collapsed={!!collapsed['ttmrev']}
              onToggle={() => toggle('ttmrev')}
              periods={periods}
            />
            {!collapsed['ttmrev'] && (
              <>
                {operating_metrics.ttm_revenue.revenue_sections.map(sec => {
                  const secKey = `ttmrev-${sec.section}`;
                  return (
                    <React.Fragment key={secKey}>
                      <tr
                        className={`border-b border-gray-800/60 ${sec.categories.length > 0 ? 'cursor-pointer hover:bg-gray-800/30' : ''}`}
                        onClick={sec.categories.length > 0 ? () => toggle(secKey) : undefined}
                      >
                        <td className="sticky left-0 z-10 bg-gray-900 pl-4 md:pl-8 pr-3 md:pr-5 py-2 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 text-[11px] md:text-xs select-none w-3 inline-block">
                              {sec.categories.length > 0 ? (collapsed[secKey] ? '▶' : '▼') : ''}
                            </span>
                            <span className="font-bold text-white text-[11px] md:text-xs">{sec.section}</span>
                          </div>
                        </td>
                        {periods.map(p => (
                          <td key={p} className="text-right px-3 md:px-6 py-2 font-bold text-white text-[11px] md:text-xs whitespace-nowrap">
                            {sec.values[p] != null ? <Tip k="ttm_revenue">{formatVal(sec.values[p] as number)}</Tip> : ''}
                          </td>
                        ))}
                      </tr>
                      {!collapsed[secKey] && sec.categories.map(cat => (
                        <tr key={`${secKey}-${cat.category}`} className="border-b border-gray-800/40 hover:bg-[#131b2a]">
                          <td className="sticky left-0 z-10 bg-gray-900 pl-6 md:pl-16 pr-3 md:pr-5 py-1.5 text-gray-400 text-[11px] md:text-xs md:whitespace-nowrap">{cat.category}</td>
                          {periods.map(p => (
                            <td key={p} className="text-right px-3 md:px-6 py-1.5 text-gray-300 text-[11px] md:text-xs whitespace-nowrap">
                              {cat.values[p] != null ? formatVal(cat.values[p] as number) : ''}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
                <tr className="border-b border-gray-800/60 hover:bg-gray-800/30">
                  <td className="sticky left-0 z-10 bg-gray-900 pl-5 md:pl-[52px] pr-3 md:pr-5 py-2 text-gray-400 text-[11px] md:text-xs md:whitespace-nowrap">Revenue per Customer Data</td>
                  {periods.map(p => (
                    <td key={p} className="text-right px-3 md:px-6 py-2 text-gray-300 text-[11px] md:text-xs whitespace-nowrap">
                      {customerData?.summary.ttm_revenue[p] != null ? <Tip k="ttm_revenue">{formatVal(customerData.summary.ttm_revenue[p]!)}</Tip> : ''}
                    </td>
                  ))}
                </tr>
              </>
            )}

            {/* Revenue Mix % — only rendered if categories exist */}
            {operating_metrics.revenue_mix.has_revenue_mix && (
              <>
                <MetricGroupRow
                  label="Revenue Mix %"
                  collapsed={!!collapsed['mix']}
                  onToggle={() => toggle('mix')}
                  periods={periods}
                />
                {!collapsed['mix'] && operating_metrics.revenue_mix.revenue_sections.map(sec => {
                  const secKey = `mix-${sec.section}`;
                  return (
                    <React.Fragment key={secKey}>
                      <tr
                        className={`border-b border-gray-800/60 ${sec.categories.length > 0 ? 'cursor-pointer hover:bg-gray-800/30' : ''}`}
                        onClick={sec.categories.length > 0 ? () => toggle(secKey) : undefined}
                      >
                        <td className="sticky left-0 z-10 bg-gray-900 pl-4 md:pl-8 pr-3 md:pr-5 py-2 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 text-[11px] md:text-xs select-none w-3 inline-block">
                              {sec.categories.length > 0 ? (collapsed[secKey] ? '▶' : '▼') : ''}
                            </span>
                            <span className="font-bold text-white text-[11px] md:text-xs">{sec.section}</span>
                          </div>
                        </td>
                        {periods.map(p => (
                          <td key={p} className="text-right px-3 md:px-6 py-2 font-bold text-white text-[11px] md:text-xs whitespace-nowrap">
                            <Tip k="revenue_mix_pct">{formatPct(sec.values[p])}</Tip>
                          </td>
                        ))}
                      </tr>
                      {!collapsed[secKey] && sec.categories.map(cat => (
                        <tr key={`${secKey}-${cat.category}`} className="border-b border-gray-800/40 hover:bg-[#131b2a]">
                          <td className="sticky left-0 z-10 bg-gray-900 pl-6 md:pl-16 pr-3 md:pr-5 py-1.5 text-gray-400 text-[11px] md:text-xs md:whitespace-nowrap">{cat.category}</td>
                          {periods.map(p => (
                            <td key={p} className="text-right px-3 md:px-6 py-1.5 text-gray-300 text-[11px] md:text-xs whitespace-nowrap">
                              <Tip k="revenue_mix_pct">{formatPct(cat.values[p])}</Tip>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </>
            )}

            {/* Margins */}
            <MetricGroupRow
              label="Margins"
              collapsed={!!collapsed['margins']}
              onToggle={() => toggle('margins')}
              periods={periods}
            />
            {!collapsed['margins'] && (() => {
              const m = operating_metrics.margins;
              const rows: { label: string; values: Record<string, number | null>; kpiKey: string }[] = [];
              if (m.has_net_revenue_margin) rows.push({ label: 'Net Revenue Margin', values: m.net_revenue_margin, kpiKey: 'net_revenue_margin_pct' });
              rows.push({ label: 'Gross Margin',   values: m.gross_margin, kpiKey: 'gross_margin_pct' });
              rows.push({ label: 'EBITDA Margin',  values: m.ebitda_margin, kpiKey: 'ebitda_margin_pct' });
              if (m.has_net_profit_margin) rows.push({ label: 'Net Profit Margin', values: m.net_profit_margin, kpiKey: 'net_profit_margin_pct' });
              return rows.map(row => (
                <tr key={row.label} className="border-b border-gray-800/60 hover:bg-gray-800/30">
                  <td className="sticky left-0 z-10 bg-gray-900 pl-5 md:pl-[52px] pr-3 md:pr-5 py-2 text-gray-400 text-[11px] md:text-xs md:whitespace-nowrap">{row.label}</td>
                  {periods.map(p => (
                    <td key={p} className="text-right px-3 md:px-6 py-2 text-gray-300 text-[11px] md:text-xs whitespace-nowrap">
                      <Tip k={row.kpiKey}>{formatPct(row.values[p])}</Tip>
                    </td>
                  ))}
                </tr>
              ));
            })()}

            {/* Capital Efficiency */}
            <MetricGroupRow
              label="Capital Efficiency"
              collapsed={!!collapsed['capeff']}
              onToggle={() => toggle('capeff')}
              periods={periods}
            />
            {!collapsed['capeff'] && (() => {
              const ce = operating_metrics.capital_efficiency;

              // Compute RRR Added per Customer Data (quarterly change in customer run-rate revenue)
              const QUARTER_END = new Set(['03', '06', '09', '12']);
              const isQE = (p: string) => QUARTER_END.has(p.split('-')[1]);
              const qePeriods = periods.filter(isQE);

              // Build per-period Customer Data capital efficiency
              const revDataCE: Record<string, { rrr_added: number | null; burn_mult: number | null; rrr_burn: number | null }> = {};
              for (const p of periods) {
                if (!customerData || (pnl.period_type === 'monthly' && !isQE(p))) {
                  revDataCE[p] = { rrr_added: null, burn_mult: null, rrr_burn: null };
                  continue;
                }
                const rrr = customerData.summary.run_rate_revenue;
                const qeList = pnl.period_type === 'quarterly' ? periods : qePeriods;
                const qeIdx = qeList.indexOf(p);
                const priorQE = qeIdx > 0 ? qeList[qeIdx - 1] : null;
                const rrrAdded = priorQE != null && rrr[p] != null && rrr[priorQE] != null
                  ? rrr[p] - rrr[priorQE] : null;
                const qEbitda = ce[p]?.quarterly_ebitda ?? null;
                let burnMult: number | null = null;
                let rrrBurn: number | null = null;
                if (rrrAdded != null && qEbitda != null && qEbitda < 0 && rrrAdded !== 0) {
                  burnMult = Math.round(Math.abs(qEbitda) / Math.abs(rrrAdded) * 100) / 100;
                  rrrBurn = Math.round(Math.abs(rrrAdded) / Math.abs(qEbitda) * 100) / 100;
                }
                revDataCE[p] = { rrr_added: rrrAdded, burn_mult: burnMult, rrr_burn: rrrBurn };
              }

              type CapRow =
                | { label: string; fmt: 'val' | 'mult'; source: 'pnl'; key: keyof CapEffPeriod; kpiKey?: string }
                | { label: string; fmt: 'val' | 'mult'; source: 'revdata'; rdKey: 'rrr_added' | 'burn_mult' | 'rrr_burn'; kpiKey?: string };
              const rows: CapRow[] = [
                { label: 'RRR Added per Q per P&L',                           fmt: 'val',  source: 'pnl', key: 'rrr_added_pnl' },
                { label: 'RRR Added per Q per Customer Data',                 fmt: 'val',  source: 'revdata', rdKey: 'rrr_added' },
                { label: 'Quarterly EBITDA',                                  fmt: 'val',  source: 'pnl', key: 'quarterly_ebitda' },
                { label: 'Burn Multiple per P&L',                             fmt: 'mult', source: 'pnl', key: 'burn_multiple_pnl', kpiKey: 'burn_multiple_pnl' },
                { label: 'Burn Multiple per Customer Data',                   fmt: 'mult', source: 'revdata', rdKey: 'burn_mult', kpiKey: 'burn_multiple_revdata' },
                { label: 'RRR Added / Burn per P&L',                          fmt: 'mult', source: 'pnl', key: 'rrr_burn_pnl', kpiKey: 'rrr_burn_pnl' },
                { label: 'RRR Added / Burn per Customer Data',                fmt: 'mult', source: 'revdata', rdKey: 'rrr_burn', kpiKey: 'rrr_burn_revdata' },
              ];
              return rows.map((row, idx) => (
                <tr key={idx} className="border-b border-gray-800/60 hover:bg-gray-800/30">
                  <td className="sticky left-0 z-10 bg-gray-900 pl-5 md:pl-[52px] pr-3 md:pr-5 py-2 text-gray-400 text-[11px] md:text-xs md:whitespace-nowrap">{row.label}</td>
                  {periods.map(p => {
                    let v: number | null = null;
                    if (row.source === 'pnl') {
                      v = ce[p]?.[row.key] ?? null;
                    } else {
                      v = revDataCE[p]?.[row.rdKey] ?? null;
                    }
                    const formatted = row.fmt === 'mult' ? formatMultiple(v) : v === null ? '' : formatVal(v);
                    return (
                      <td key={p} className="text-right px-3 md:px-6 py-2 text-gray-300 text-[11px] md:text-xs whitespace-nowrap">
                        <Tip k={row.kpiKey}>{formatted}</Tip>
                      </td>
                    );
                  })}
                </tr>
              ));
            })()}

            {/* Sales Efficiency (Magic Number) */}
            <MetricGroupRow
              label="Sales Efficiency (Magic Number)"
              collapsed={!!collapsed['saleseff']}
              onToggle={() => toggle('saleseff')}
              periods={periods}
            />
            {!collapsed['saleseff'] && (() => {
              const se = operating_metrics.sales_efficiency;

              // Compute Magic Number per Customer Data (RRR Added per Customer Data / Prior Quarter S&M)
              const QUARTER_END = new Set(['03', '06', '09', '12']);
              const isQE = (p: string) => QUARTER_END.has(p.split('-')[1]);
              const qePeriods = periods.filter(isQE);

              const revDataMagic: Record<string, number | null> = {};
              for (const p of periods) {
                if (!customerData || (pnl.period_type === 'monthly' && !isQE(p))) {
                  revDataMagic[p] = null;
                  continue;
                }
                const rrr = customerData.summary.run_rate_revenue;
                const qeList = pnl.period_type === 'quarterly' ? periods : qePeriods;
                const qeIdx = qeList.indexOf(p);
                const priorQE = qeIdx > 0 ? qeList[qeIdx - 1] : null;
                const priorPriorQE = qeIdx > 1 ? qeList[qeIdx - 2] : null;
                const rrrAdded = priorQE != null && rrr[p] != null && rrr[priorQE] != null
                  ? rrr[p] - rrr[priorQE] : null;
                // Prior quarter S&M: use the quarterly_sm from the prior quarter-end
                const priorSM = priorQE != null ? se[priorQE]?.quarterly_sm ?? null : null;
                let magic: number | null = null;
                if (rrrAdded != null && priorSM != null && priorSM !== 0) {
                  magic = Math.round(rrrAdded / Math.abs(priorSM) * 100) / 100;
                }
                revDataMagic[p] = magic;
              }

              type SalesRow =
                | { label: string; key: keyof SalesEffPeriod; fmt: 'val' | 'mult'; source: 'pnl'; kpiKey?: string }
                | { label: string; fmt: 'mult'; source: 'revdata'; kpiKey?: string };
              const rows: SalesRow[] = [
                { label: 'Quarterly S&M',                              key: 'quarterly_sm', fmt: 'val',  source: 'pnl' },
                { label: 'Magic Number per P&L',                       key: 'magic_pnl',    fmt: 'mult', source: 'pnl', kpiKey: 'magic_number_pnl' },
                { label: 'Magic Number per Customer Data',             fmt: 'mult', source: 'revdata', kpiKey: 'magic_number_revdata' },
              ];
              return rows.map((row, idx) => (
                <tr key={idx} className="border-b border-gray-800/60 hover:bg-gray-800/30">
                  <td className="sticky left-0 z-10 bg-gray-900 pl-5 md:pl-[52px] pr-3 md:pr-5 py-2 text-gray-400 text-[11px] md:text-xs md:whitespace-nowrap">{row.label}</td>
                  {periods.map(p => {
                    let v: number | null = null;
                    if (row.source === 'pnl') {
                      v = se[p]?.[row.key] ?? null;
                    } else {
                      v = revDataMagic[p] ?? null;
                    }
                    const formatted = row.fmt === 'mult' ? formatMultiple(v) : v === null ? '' : formatVal(v);
                    return (
                      <td key={p} className="text-right px-3 md:px-6 py-2 text-gray-300 text-[11px] md:text-xs whitespace-nowrap">
                        <Tip k={row.kpiKey}>{formatted}</Tip>
                      </td>
                    );
                  })}
                </tr>
              ));
            })()}

            {/* % of Revenue */}
            {(() => {
              const por = operating_metrics.pct_of_revenue;
              const porKey = 'por';
              return (
                <>
                  <MetricGroupRow
                    label={por.section_label}
                    collapsed={!!collapsed[porKey]}
                    onToggle={() => toggle(porKey)}
                    periods={periods}
                  />
                  {!collapsed[porKey] && (
                    <>
                      {/* Total Opex row */}
                      <tr className="border-b border-gray-800/60 hover:bg-gray-800/30">
                        <td className="sticky left-0 z-10 bg-gray-900 pl-4 md:pl-8 pr-3 md:pr-5 py-2 font-bold text-white text-[11px] md:text-xs whitespace-nowrap">
                          Total Opex
                        </td>
                        {periods.map(p => (
                          <td key={p} className="text-right px-3 md:px-6 py-2 font-bold text-white text-[11px] md:text-xs whitespace-nowrap">
                            <Tip k="pct_of_revenue">{formatPct(por.total[p])}</Tip>
                          </td>
                        ))}
                      </tr>
                      {/* Category rows */}
                      {por.has_categories && por.categories.map(cat => (
                        <tr key={cat.category} className="border-b border-gray-800/40 hover:bg-[#131b2a]">
                          <td className="sticky left-0 z-10 bg-gray-900 pl-6 md:pl-16 pr-3 md:pr-5 py-1.5 text-gray-400 text-[11px] md:text-xs md:whitespace-nowrap">
                            {cat.category}
                          </td>
                          {periods.map(p => (
                            <td key={p} className="text-right px-3 md:px-6 py-1.5 text-gray-300 text-[11px] md:text-xs whitespace-nowrap">
                              <Tip k="pct_of_revenue">{formatPct(cat.values[p])}</Tip>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </>
                  )}
                </>
              );
            })()}

            {/* Rule of 40 */}
            <MetricGroupRow
              label="Rule of 40"
              collapsed={!!collapsed['ro40']}
              onToggle={() => toggle('ro40')}
              periods={periods}
            />
            {!collapsed['ro40'] && (() => {
              const ro40 = operating_metrics.rule_of_40;
              const rows: { label: string; values: Record<string, number | null>; kpiKey: string }[] = [
                { label: 'Rule of 40 (YoY Growth)',     values: ro40.yoy, kpiKey: 'rule_of_40_yoy' },
                { label: 'Rule of 40 (TTM YoY Growth)', values: ro40.ttm, kpiKey: 'rule_of_40_ttm' },
              ];
              return rows.map(row => (
                <tr key={row.label} className="border-b border-gray-800/60 hover:bg-gray-800/30">
                  <td className="sticky left-0 z-10 bg-gray-900 pl-5 md:pl-[52px] pr-3 md:pr-5 py-2 text-gray-400 text-[11px] md:text-xs md:whitespace-nowrap">{row.label}</td>
                  {periods.map(p => (
                    <td key={p} className="text-right px-3 md:px-6 py-2 text-gray-300 text-[11px] md:text-xs whitespace-nowrap">
                      <Tip k={row.kpiKey}>{formatPct(row.values[p])}</Tip>
                    </td>
                  ))}
                </tr>
              ));
            })()}

            {/* Cash Runway (Months) — hidden entirely if no cash data */}
            {operating_metrics.cash_runway.has_cash && (() => {
              const cr = operating_metrics.cash_runway;
              type CrRow =
                | { label: string; values: Record<string, number | null>; fmt: 'val' | 'months'; placeholder?: false; kpiKey?: string }
                | { label: string; placeholder: true };
              const rows: CrRow[] = [
                { label: 'Cash Balance',          fmt: 'val',    values: cr.cash_balance  },
                { label: 'EBITDA',                fmt: 'val',    values: cr.ebitda_values },
                cr.has_cfo
                  ? { label: 'CFO',               fmt: 'val',    values: cr.cfo_values }
                  : { label: 'CFO',               placeholder: true },
                { label: 'Cash Runway (EBITDA)',  fmt: 'months', values: cr.ebitda, kpiKey: 'cash_runway_ebitda' },
                cr.has_cfo
                  ? { label: 'Cash Runway (CFO)', fmt: 'months', values: cr.cfo, kpiKey: 'cash_runway_cfo' }
                  : { label: 'Cash Runway (CFO)', placeholder: true },
              ];
              return (
                <>
                  <MetricGroupRow
                    label="Cash Runway (Months)"
                    collapsed={!!collapsed['cashrunway']}
                    onToggle={() => toggle('cashrunway')}
                    periods={periods}
                  />
                  {!collapsed['cashrunway'] && rows.map((row, idx) => (
                    <tr key={idx} className="border-b border-gray-800/60 hover:bg-gray-800/30">
                      <td className="sticky left-0 z-10 bg-gray-900 pl-5 md:pl-[52px] pr-3 md:pr-5 py-2 text-gray-400 text-[11px] md:text-xs md:whitespace-nowrap">{row.label}</td>
                      {periods.map(p => {
                        if (row.placeholder) return <td key={p} className="text-right px-3 md:px-6 py-2 text-gray-300 text-[11px] md:text-xs whitespace-nowrap" />;
                        const formatted = row.fmt === 'months'
                          ? formatMonths(row.values[p])
                          : formatVal((row.values[p] as number) ?? 0);
                        return (
                          <td key={p} className="text-right px-3 md:px-6 py-2 text-gray-300 text-[11px] md:text-xs whitespace-nowrap">
                            <Tip k={row.kpiKey}>{formatted}</Tip>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </>
              );
            })()}

            {/* ACVs */}
            <MetricGroupRow
              label="ACVs"
              collapsed={!!collapsed['acvs']}
              onToggle={() => toggle('acvs')}
              periods={periods}
            />
            {!collapsed['acvs'] && (() => {
              if (!customerData) {
                return (
                  <>
                    <PlaceholderRow label="ACV per Customer"         periods={periods} />
                    <PlaceholderRow label="New ACV per New Customer" periods={periods} />
                  </>
                );
              }
              const mb = customerData.mrr_build;
              const cb = customerData.customer_build;
              const acvMult = customerData.period_type === 'quarterly' ? 4 : 12;
              const rows = [
                { label: 'ACV per Customer', kpiKey: 'acv_per_customer', calc: (p: string) => {
                  const ending = mb[p]?.ending ?? 0;
                  const custCount = cb[p]?.ending ?? 0;
                  return custCount > 0 ? (ending * acvMult) / custCount : null;
                }},
                { label: 'New ACV per New Customer', kpiKey: 'new_acv_per_new_customer', calc: (p: string) => {
                  const newMRR = mb[p]?.new ?? 0;
                  const newCust = cb[p]?.additions ?? 0;
                  return newCust > 0 ? (newMRR * acvMult) / newCust : null;
                }},
              ];
              return rows.map(row => (
                <tr key={row.label} className="border-b border-gray-800/60 hover:bg-gray-800/30">
                  <td className="sticky left-0 z-10 bg-gray-900 pl-5 md:pl-[52px] pr-3 md:pr-5 py-2 text-gray-400 text-[11px] md:text-xs md:whitespace-nowrap">{row.label}</td>
                  {periods.map(p => {
                    const v = row.calc(p);
                    return (
                      <td key={p} className="text-right px-3 md:px-6 py-2 text-gray-300 text-[11px] md:text-xs whitespace-nowrap">
                        {v != null ? <Tip k={row.kpiKey}>{formatVal(v)}</Tip> : ''}
                      </td>
                    );
                  })}
                </tr>
              ));
            })()}

            {/* Revenue Retention — NRR / GRR from customer analytics */}
            <MetricGroupRow
              label="Revenue Retention"
              collapsed={!!collapsed['revretention']}
              onToggle={() => toggle('revretention')}
              periods={periods}
            />
            {!collapsed['revretention'] && (() => {
              if (!customerData) {
                return (
                  <>
                    <PlaceholderRow label="NRR (Net Revenue Retention)"   periods={periods} />
                    <PlaceholderRow label="GRR (Gross Revenue Retention)" periods={periods} />
                    <PlaceholderRow label="SaaS Quick Ratio" periods={periods} />
                  </>
                );
              }
              const rows: { label: string; values: Record<string, number | null>; fmt?: 'mult'; kpiKey: string }[] = [
                { label: 'NRR (Net Revenue Retention)',   values: customerData.nrr, kpiKey: 'nrr' },
                { label: 'GRR (Gross Revenue Retention)', values: customerData.grr, kpiKey: 'grr' },
              ];

              // SaaS Quick Ratio: (New + Resurrection + Upsell) / |Contraction + Churned|
              const quickRatio: Record<string, number | null> = {};
              for (const p of periods) {
                const b = customerData.mrr_build?.[p];
                if (!b) { quickRatio[p] = null; continue; }
                const pos = (b.new || 0) + (b.resurrection || 0) + (b.upsell || 0);
                const neg = Math.abs((b.contraction || 0) + (b.churned || 0));
                quickRatio[p] = neg > 0 ? Math.round(pos / neg * 100) / 100 : null;
              }
              rows.push({ label: 'SaaS Quick Ratio', values: quickRatio, fmt: 'mult', kpiKey: 'saas_quick_ratio' });

              return rows.map(row => (
                <tr key={row.label} className="border-b border-gray-800/60 hover:bg-gray-800/30">
                  <td className="sticky left-0 z-10 bg-gray-900 pl-5 md:pl-[52px] pr-3 md:pr-5 py-2 text-gray-400 text-[11px] md:text-xs md:whitespace-nowrap">{row.label}</td>
                  {periods.map(p => (
                    <td key={p} className="text-right px-3 md:px-6 py-2 text-gray-300 text-[11px] md:text-xs whitespace-nowrap">
                      <Tip k={row.kpiKey}>{row.fmt === 'mult' ? formatMultiple(row.values[p]) : formatPct(row.values[p])}</Tip>
                    </td>
                  ))}
                </tr>
              ));
            })()}

            {/* ══ BASE Analysis ══ */}
            <SectionLabelRow label="BASE Analysis" periods={periods} />

            {/* Customer Build */}
            <MetricGroupRow
              label="Customer Build"
              collapsed={!!collapsed['custbuild']}
              onToggle={() => toggle('custbuild')}
              periods={periods}
            />
            {!collapsed['custbuild'] && (() => {
              if (!customerData) {
                return (
                  <>
                    <PlaceholderRow label="Beginning"        periods={periods} />
                    <PlaceholderRow label="Addition"         periods={periods} />
                    <PlaceholderRow label="Subtraction"      periods={periods} />
                    <PlaceholderRow label="Ending"           periods={periods} />
                    <PlaceholderRow label="Customer Churn %" periods={periods} indent />
                  </>
                );
              }
              const cb = customerData.customer_build;
              const rows: { label: string; key: keyof CustomerBuildPeriod; fmt: 'count' | 'pct'; indent?: boolean; kpiKey?: string }[] = [
                { label: 'Beginning',        key: 'beginning',    fmt: 'count' },
                { label: 'Addition',         key: 'additions',    fmt: 'count' },
                { label: 'Subtraction',      key: 'subtractions', fmt: 'count' },
                { label: 'Ending',           key: 'ending',       fmt: 'count' },
                { label: 'Customer Churn %', key: 'churn_pct',    fmt: 'pct', indent: true, kpiKey: 'customer_churn_pct' },
              ];
              return rows.map(row => (
                <tr key={row.label} className="border-b border-gray-800/60 hover:bg-gray-800/30">
                  <td className={`sticky left-0 z-10 bg-gray-900 ${row.indent ? 'pl-6 md:pl-[68px]' : 'pl-5 md:pl-[52px]'} pr-3 md:pr-5 py-2 text-gray-400 text-[11px] md:text-xs md:whitespace-nowrap`}>{row.label}</td>
                  {periods.map(p => {
                    const period = cb[p];
                    if (!period) return <td key={p} className="text-right px-3 md:px-6 py-2 text-gray-300 text-[11px] md:text-xs whitespace-nowrap" />;
                    const v = period[row.key];
                    const formatted = row.fmt === 'pct' ? formatPct(v != null ? -(v as number) : null) : formatCount(v as number);
                    return (
                      <td key={p} className="text-right px-3 md:px-6 py-2 text-gray-300 text-[11px] md:text-xs whitespace-nowrap">
                        <Tip k={row.kpiKey}>{formatted}</Tip>
                      </td>
                    );
                  })}
                </tr>
              ));
            })()}

            {/* Revenue Build */}
            <MetricGroupRow
              label="Revenue Build"
              collapsed={!!collapsed['mrrbuild']}
              onToggle={() => toggle('mrrbuild')}
              periods={periods}
            />
            {!collapsed['mrrbuild'] && (() => {
              if (!customerData) {
                return (
                  <>
                    <PlaceholderRow label="Beginning Rev"    periods={periods} />
                    <PlaceholderRow label="New Rev"          periods={periods} />
                    <PlaceholderRow label="Resurrection Rev" periods={periods} />
                    <PlaceholderRow label="Upsell Rev"       periods={periods} />
                    <PlaceholderRow label="Contraction Rev"  periods={periods} />
                    <PlaceholderRow label="Churned Rev"      periods={periods} />
                    <PlaceholderRow label="Ending Rev"       periods={periods} />
                  </>
                );
              }
              const mb = customerData.mrr_build;
              const rows: { label: string; key: keyof MrrBuildPeriod; fmt: 'val' | 'pct' }[] = [
                { label: 'Beginning Rev',    key: 'beginning',    fmt: 'val' },
                { label: 'New Rev',          key: 'new',          fmt: 'val' },
                { label: 'Resurrection Rev', key: 'resurrection', fmt: 'val' },
                { label: 'Upsell Rev',       key: 'upsell',       fmt: 'val' },
                { label: 'Contraction Rev',  key: 'contraction',  fmt: 'val' },
                { label: 'Churned Rev',      key: 'churned',      fmt: 'val' },
                { label: 'Ending Rev',       key: 'ending',       fmt: 'val' },
              ];
              return rows.map(row => (
                <tr key={row.label} className="border-b border-gray-800/60 hover:bg-gray-800/30">
                  <td className="sticky left-0 z-10 bg-gray-900 pl-5 md:pl-[52px] pr-3 md:pr-5 py-2 text-gray-400 text-[11px] md:text-xs md:whitespace-nowrap">{row.label}</td>
                  {periods.map(p => {
                    const period = mb[p];
                    if (!period) return <td key={p} className="text-right px-3 md:px-6 py-2 text-gray-300 text-[11px] md:text-xs whitespace-nowrap" />;
                    const v = period[row.key];
                    return (
                      <td key={p} className="text-right px-3 md:px-6 py-2 text-gray-300 text-[11px] md:text-xs whitespace-nowrap">
                        {formatVal(v as number)}
                      </td>
                    );
                  })}
                </tr>
              ));
            })()}

            {/* Run-Rate Revenue Build */}
            <MetricGroupRow
              label="Run-Rate Revenue Build"
              collapsed={!!collapsed['arrbuild']}
              onToggle={() => toggle('arrbuild')}
              periods={periods}
            />
            {!collapsed['arrbuild'] && (() => {
              if (!customerData) {
                return (
                  <>
                    <PlaceholderRow label="Beginning RRR"      periods={periods} />
                    <PlaceholderRow label="New RRR"            periods={periods} />
                    <PlaceholderRow label="Resurrection RRR"   periods={periods} />
                    <PlaceholderRow label="Upsell RRR"         periods={periods} />
                    <PlaceholderRow label="Contraction RRR"    periods={periods} />
                    <PlaceholderRow label="Churned RRR"        periods={periods} />
                    <PlaceholderRow label="Ending RRR"         periods={periods} />
                    <PlaceholderRow label="Rev Net Churn %"    periods={periods} indent />
                    <PlaceholderRow label="Rev Gross Churn %"  periods={periods} indent />
                  </>
                );
              }
              const mb = customerData.mrr_build;
              const rrrMult = customerData.period_type === 'quarterly' ? 4 : 12;
              const arrValKeys: { label: string; key: keyof MrrBuildPeriod }[] = [
                { label: 'Beginning RRR',    key: 'beginning'    },
                { label: 'New RRR',          key: 'new'          },
                { label: 'Resurrection RRR', key: 'resurrection' },
                { label: 'Upsell RRR',       key: 'upsell'       },
                { label: 'Contraction RRR',  key: 'contraction'  },
                { label: 'Churned RRR',      key: 'churned'      },
                { label: 'Ending RRR',       key: 'ending'       },
              ];
              const pctRows: { label: string; key: keyof MrrBuildPeriod; kpiKey: string }[] = [
                { label: 'Rev Net Churn %',   key: 'net_churn_pct',   kpiKey: 'mrr_net_churn_pct' },
                { label: 'Rev Gross Churn %',  key: 'gross_churn_pct', kpiKey: 'mrr_gross_churn_pct' },
              ];
              return (
                <>
                  {arrValKeys.map(row => (
                    <tr key={row.label} className="border-b border-gray-800/60 hover:bg-gray-800/30">
                      <td className="sticky left-0 z-10 bg-gray-900 pl-5 md:pl-[52px] pr-3 md:pr-5 py-2 text-gray-400 text-[11px] md:text-xs md:whitespace-nowrap">{row.label}</td>
                      {periods.map(p => {
                        const period = mb[p];
                        if (!period) return <td key={p} className="text-right px-3 md:px-6 py-2 text-gray-300 text-[11px] md:text-xs whitespace-nowrap" />;
                        const mrrVal = period[row.key] as number;
                        return (
                          <td key={p} className="text-right px-3 md:px-6 py-2 text-gray-300 text-[11px] md:text-xs whitespace-nowrap">
                            {formatVal(mrrVal * rrrMult)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {pctRows.map(row => (
                    <tr key={row.label} className="border-b border-gray-800/60 hover:bg-gray-800/30">
                      <td className="sticky left-0 z-10 bg-gray-900 pl-6 md:pl-[68px] pr-3 md:pr-5 py-2 text-gray-400 text-[11px] md:text-xs md:whitespace-nowrap">{row.label}</td>
                      {periods.map(p => {
                        const period = mb[p];
                        if (!period) return <td key={p} className="text-right px-3 md:px-6 py-2 text-gray-300 text-[11px] md:text-xs whitespace-nowrap" />;
                        return (
                          <td key={p} className="text-right px-3 md:px-6 py-2 text-gray-300 text-[11px] md:text-xs whitespace-nowrap">
                            <Tip k={row.kpiKey}>{formatPct(period[row.key] as number | null)}</Tip>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </>
              );
            })()}

            {/* ══ Balance Sheet ══ */}
            {balance_sheet_sections.length > 0 && (
              <>
                <SectionLabelRow label="Balance Sheet" periods={periods} />
                {balance_sheet_sections.map(sec => (
                  <PnlSectionBlock
                    key={`bs-${sec.section}`}
                    sec={sec}
                    periods={periods}
                    collapsed={!!collapsed[`bs-${sec.section}`]}
                    onToggle={() => toggle(`bs-${sec.section}`)}
                  />
                ))}
              </>
            )}

            {/* ══ Cash Flow Statement ══ */}
            {cash_flow_sections.length > 0 && (
              <>
                <SectionLabelRow label="Cash Flow Statement" periods={periods} />
                {cash_flow_sections.map(sec => (
                  <PnlSectionBlock
                    key={`cf-${sec.section}`}
                    sec={sec}
                    periods={periods}
                    collapsed={!!collapsed[`cf-${sec.section}`]}
                    onToggle={() => toggle(`cf-${sec.section}`)}
                  />
                ))}
              </>
            )}

          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Dashboard View ────────────────────────────────────────────────────────────

export default function DashboardView({ projectId, unitScale }: { projectId: string; unitScale: UnitScale }) {
  _scale = unitScale;
  const [pnl, setPnl]                       = useState<PnlData | null>(null);
  const [customerData, setCustomerData]      = useState<CustomerAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      authedFetch(apiUrl('/api/financials/pnl', projectId)).then(res => {
        if (!res.ok) throw new Error('PNL fetch failed');
        return res.json();
      }),
      authedFetch(apiUrl('/api/customers/analytics', projectId))
        .then(res => res.ok ? res.json() : null)
        .catch(() => null),  // Customer data is optional — don't block dashboard
    ])
      .then(([pnlData, custData]) => {
        setPnl(pnlData);
        setCustomerData(custData);
        setLoading(false);
      })
      .catch(() => { setError('Could not connect to backend'); setLoading(false); });
  }, [projectId]);

  if (loading) return <p className="text-gray-500 text-sm">Loading...</p>;
  if (error)   return <p className="text-red-400 text-sm">{error}</p>;
  if (!pnl)    return null;

  return (
    <div className="space-y-4">
      <DashboardTable key={projectId} pnl={pnl} customerData={customerData} projectId={projectId} />
    </div>
  );
}
